import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Circle from "../assets/Vector-1.svg";
import Collar from "../assets/Rectangle(1).png";
import J from "../assets/Vector.svg";
import {
  ArrowRight,
  ArrowDown,
  BarChart3,
  Bot,
  Brain,
  Calendar,
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Check,
  FileSearch,
  FileText,
  FolderOpen,
  Gauge,
  Headphones,
  LayoutDashboard,
  Lock,
  Maximize,
  Menu,
  Monitor,
  Play,
  Receipt,
  Rocket,
  Scale,
  ShieldCheck,
  Sparkles,
  Users,
  UsersRound,
  Volume2,
  Wallet,
  Workflow,
  X,
  Zap,
} from "lucide-react";

/* Live screens captured from the new portal (jurident.com/new/portal/*),
   shipped in /public/new-ui */
const SHOTS = {
  dashboard: "/new-ui/dashboard.jpg",
  teams: "/new-ui/teams.jpg",
  tasks: "/new-ui/tasks-board.jpg",
  calendar: "/new-ui/calendar.jpg",
  cases: "/new-ui/cases.jpg",
  caseDetails: "/new-ui/case-details.jpg",
  billing: "/new-ui/billing.jpg",
  documents: "/new-ui/documents.jpg",
  clients: "/new-ui/clients.jpg",
  ai: "/new-ui/ai-assistant.jpg",
  oldPortal: "/new-ui/old-portal.jpg",
};

const PREVIEW = SHOTS.dashboard;

const FONT = {
  fontFamily:
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
};

/* ------------------------------------------------------------------ */
/*  LOGO                                                               */
/* ------------------------------------------------------------------ */
function Logo({ onClick }) {
  return (
    <div
      onClick={onClick}
      className="flex items-end gap-[2px] cursor-pointer select-none shrink-0"
    >
      <div className="relative w-[22px] h-[38px]">
        <img src={J} alt="" className="absolute bottom-0 left-0 w-[22px]" />
        <img src={Circle} alt="" className="absolute top-0 left-[20%] w-[14px]" />
        <img src={Collar} alt="" className="absolute top-[6%] left-[20%] w-[14px]" />
      </div>
      <span className="text-white font-semibold text-[20px] tracking-[0.14em] leading-none pb-[3px]">
        URIDENT
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NAVBAR                                                             */
/* ------------------------------------------------------------------ */
const NAV_ITEMS = [
  { name: "Features", dropdown: true },
  { name: "Preview" },
  { name: "AI" },
  { name: "Pricing" },
  { name: "Resources", dropdown: true },
  { name: "Support" },
];

function TopNav({ navigate }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="absolute top-0 left-0 w-full z-50">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo onClick={() => navigate("/")} />

        {/* desktop links */}
        <ul className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {NAV_ITEMS.map((item) => (
            <li key={item.name}>
              <button className="flex items-center gap-1 text-[14px] font-medium text-white/75 hover:text-white transition-colors">
                {item.name}
                {item.dropdown && <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </li>
          ))}
        </ul>

        {/* desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => navigate("/portal")}
            className="px-6 py-2 rounded-lg text-[14px] font-medium text-white border border-white/20 hover:bg-white/10 transition-colors"
          >
            Login
          </button>
          <button
            onClick={() => navigate("/portal")}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-[14px] font-semibold text-[#1A1033] bg-[#F2B22E] hover:bg-[#e0a41f] transition-colors"
          >
            Experience Jurident 2.0 <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* mobile toggle */}
        <button
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="lg:hidden mx-4 rounded-2xl border border-white/10 bg-[#0D0527]/95 backdrop-blur-xl p-5 shadow-2xl">
          <ul className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.name}>
                <button className="w-full text-left px-3 py-3 rounded-xl text-[15px] font-medium text-white/80 hover:bg-white/5 hover:text-white transition">
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-col gap-2">
            <button
              onClick={() => navigate("/portal")}
              className="w-full py-3 rounded-lg text-[14px] font-medium text-white border border-white/20"
            >
              Login
            </button>
            <button
              onClick={() => navigate("/portal")}
              className="w-full py-3 rounded-lg text-[14px] font-semibold text-[#1A1033] bg-[#F2B22E]"
            >
              Experience Jurident 2.0 →
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/*  HERO                                                               */
/* ------------------------------------------------------------------ */
const HERO_CHIPS = [
  { icon: Brain, label: "AI Powered", color: "text-[#F471B5]" },
  { icon: ShieldCheck, label: "Secure & Reliable", color: "text-[#34D399]" },
  { icon: Zap, label: "Lightning Fast", color: "text-[#A78BFA]" },
  { icon: Scale, label: "Made for Legal Pros", color: "text-[#F2B22E]" },
];

function Hero({ navigate }) {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="relative overflow-hidden bg-[#070220]">
      {/* glows */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 520px at 78% 38%, rgba(109,58,196,0.45) 0%, rgba(109,58,196,0) 62%), radial-gradient(700px 420px at 100% 8%, rgba(64,38,150,0.5) 0%, rgba(64,38,150,0) 60%), radial-gradient(620px 380px at 4% 96%, rgba(88,46,166,0.28) 0%, rgba(88,46,166,0) 60%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }}
      />

      <TopNav navigate={navigate} />

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 pt-[130px] pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-start">
          {/* ---------------- LEFT ---------------- */}
          <div className="pt-2 lg:pt-6">
            <div className="flex items-center gap-2 text-[13px] font-medium text-[#C6B8E8]">
              <Sparkles className="w-3.5 h-3.5 text-[#F2B22E]" />
              Introducing
              <Sparkles className="w-3.5 h-3.5 text-[#F2B22E]" />
            </div>

            <div className="mt-3 flex items-center gap-3">
              <h2 className="text-white text-[30px] sm:text-[34px] font-semibold leading-none">
                Jurident 2.0
              </h2>
              <span className="px-2.5 py-[3px] rounded-md bg-[#5B4BE7] text-white text-[11px] font-semibold">
                New
              </span>
            </div>

            <h1 className="mt-4 text-white font-bold tracking-[-0.02em] text-[40px] sm:text-[52px] xl:text-[56px] leading-[1.08]">
              A Smarter Way
              <br />
              to Manage Legal
              <br />
              Practice
            </h1>

            <p className="mt-6 text-[15px] leading-[1.7] text-[#B9AFD3]">
              AI-Powered Workflows. Beautiful Dashboards.
              <br />
              Stronger Collaboration. Built for Modern Lawyers.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate("/portal")}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#F2B22E] text-[#1A1033] text-[14px] font-semibold hover:bg-[#e0a41f] transition-colors"
              >
                Experience Jurident 2.0 <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPlaying(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 bg-white/[0.04] text-white text-[14px] font-medium hover:bg-white/10 transition-colors"
              >
                <Play className="w-4 h-4 fill-white" /> Watch Demo
              </button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
              {HERO_CHIPS.map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${color}`} />
                  <span className="text-[12.5px] text-[#B9AFD3]">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ---------------- RIGHT : VIDEO CARD ---------------- */}
          <div className="lg:pt-4">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0C0428] shadow-[0_30px_90px_rgba(0,0,0,0.55)]">
              <img
                src={PREVIEW}
                alt="Jurident 2.0 dashboard preview"
                className={`w-full block transition-all duration-500 ${
                  playing ? "opacity-100" : "opacity-60 blur-[1px]"
                }`}
              />

              {!playing && (
                <div className="absolute inset-0 bg-[#0B0424]/55 flex flex-col items-center justify-center">
                  <p className="text-white text-[22px] sm:text-[26px] font-semibold text-center leading-snug">
                    See Jurident 2.0
                    <br />
                    in Action
                  </p>
                  <button
                    onClick={() => setPlaying(true)}
                    aria-label="Play overview"
                    className="mt-5 w-[62px] h-[62px] rounded-full bg-[#7C5CFF] shadow-[0_0_0_10px_rgba(124,92,255,0.18)] flex items-center justify-center hover:scale-105 transition-transform"
                  >
                    <Play className="w-6 h-6 text-white fill-white ml-[3px]" />
                  </button>
                </div>
              )}

              {/* player bar */}
              <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-white/85 tabular-nums">
                    00:03
                  </span>
                  <div className="relative flex-1 h-[3px] rounded-full bg-white/25">
                    <div className="absolute left-0 top-0 h-full w-[55%] rounded-full bg-white" />
                    <div className="absolute top-1/2 left-[55%] -translate-y-1/2 -translate-x-1/2 w-[11px] h-[11px] rounded-full bg-white" />
                  </div>
                  <span className="text-[11px] text-white/85 tabular-nums">
                    00:10
                  </span>
                  <Volume2 className="w-4 h-4 text-white/85" />
                  <Maximize className="w-4 h-4 text-white/85" />
                </div>
              </div>
            </div>

            <p className="mt-4 flex items-center justify-center gap-1.5 text-[12.5px] text-[#A99CC7]">
              <ArrowDown className="w-3.5 h-3.5" /> 10-Second Quick Overview
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  TRUST + STATS                                                      */
/* ------------------------------------------------------------------ */
const FIRMS = [
  { name: "S&A", sub: "ASSOCIATES", serif: true },
  { name: "LexDuo", sub: "", serif: false },
  { name: "JSA", sub: "ADVOCATES & SOLICITORS", serif: true },
  { name: "Veritas", sub: "Legal Solutions", serif: true },
  { name: "LEX", sub: "CHAMBERS", serif: true },
  { name: "ĀMLEGALS", sub: "LEGAL STRATEGISTS", serif: true },
];

const STATS = [
  { icon: UsersRound, value: "1200+", label: "Legal Professionals", tint: "bg-[#EEF0FF] text-[#5B4BE7]" },
  { icon: Sparkles, value: "20+", label: "AI Features", tint: "bg-[#E8F1FF] text-[#3B82F6]" },
  { icon: Gauge, value: "99.9%", label: "Uptime", tint: "bg-[#E7F8F0] text-[#10B981]" },
  { icon: Workflow, value: "50+", label: "Automation Workflows", tint: "bg-[#FFF1E6] text-[#F97316]" },
  { icon: Headphones, value: "24/7", label: "Support", tint: "bg-[#FFEBF3] text-[#EC4899]" },
];

function TrustSection() {
  return (
    <section className="bg-white py-14">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <h3 className="text-center text-[15px] font-semibold text-[#1B1533]">
          Trusted by 1200+ Legal Professionals and Law Firms
        </h3>

        <div className="mt-9 grid grid-cols-3 md:grid-cols-6 gap-y-8 gap-x-4">
          {FIRMS.map((f) => (
            <div key={f.name} className="text-center">
              <p
                className={`text-[19px] leading-tight text-[#6B7280] ${
                  f.serif ? "font-serif font-semibold" : "font-semibold"
                }`}
              >
                {f.name}
              </p>
              {f.sub && (
                <p className="mt-0.5 text-[8.5px] tracking-[0.16em] uppercase text-[#9CA3AF]">
                  {f.sub}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-[#EBECF2] bg-white shadow-[0_10px_35px_rgba(17,17,45,0.06)]">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-y sm:divide-y-0 lg:divide-x divide-[#EFF0F5]">
            {STATS.map(({ icon: Icon, value, label, tint }) => (
              <div key={label} className="flex flex-col items-center py-8 px-4">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center ${tint}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="mt-3 text-[24px] font-bold text-[#161231]">{value}</p>
                <p className="mt-1 text-[12px] text-[#7B8095] text-center">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  EXPLORE TABS                                                       */
/* ------------------------------------------------------------------ */
const TABS = [
  { name: "Dashboard", icon: LayoutDashboard, shot: SHOTS.dashboard },
  { name: "All Cases", icon: Scale, shot: SHOTS.cases },
  { name: "Case Details", icon: FileText, shot: SHOTS.caseDetails },
  { name: "Teams", icon: Users, shot: SHOTS.teams },
  { name: "Tasks Board", icon: ClipboardList, shot: SHOTS.tasks },
  { name: "Calendar", icon: Calendar, shot: SHOTS.calendar },
  { name: "AI Assistant", icon: Bot, shot: SHOTS.ai },
  { name: "Billing", icon: Receipt, shot: SHOTS.billing },
  { name: "Documents", icon: FolderOpen, shot: SHOTS.documents },
  { name: "Client Portal", icon: Monitor, shot: SHOTS.clients },
];

function ExploreSection() {
  const [active, setActive] = useState(0);

  /* warm the cache so switching tabs never shows a blank frame */
  useEffect(() => {
    TABS.forEach((t) => {
      const img = new Image();
      img.src = t.shot;
    });
  }, []);

  const shift = (dir) =>
    setActive((i) => (i + dir + TABS.length) % TABS.length);

  return (
    <section className="bg-white pb-16">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <h2 className="text-center text-[26px] sm:text-[28px] font-bold text-[#161231]">
          Explore the New Jurident 2.0
        </h2>

        <div className="mt-7 flex items-center gap-2">
          <button
            onClick={() => shift(-1)}
            aria-label="Previous tab"
            className="shrink-0 w-8 h-8 rounded-full border border-[#E6E7EE] bg-white flex items-center justify-center text-[#6B7280] hover:bg-[#F6F7FB] transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex-1 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-2 min-w-max px-0.5 py-1">
              {TABS.map(({ name, icon: Icon }, i) => (
                <button
                  key={name}
                  onClick={() => setActive(i)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap transition-all ${
                    i === active
                      ? "bg-[#5B4BE7] text-white shadow-[0_6px_18px_rgba(91,75,231,0.35)]"
                      : "bg-white text-[#4B5063] border border-[#E6E7EE] hover:border-[#C9CBDA]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {name}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => shift(1)}
            aria-label="Next tab"
            className="shrink-0 w-8 h-8 rounded-full border border-[#E6E7EE] bg-white flex items-center justify-center text-[#6B7280] hover:bg-[#F6F7FB] transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-6 rounded-2xl overflow-hidden border border-[#E9EAF1] bg-white shadow-[0_18px_50px_rgba(17,17,45,0.10)]">
          <img
            src={TABS[active].shot}
            alt={`Jurident 2.0 ${TABS[active].name} screen`}
            decoding="async"
            className="w-full block aspect-[1568/782] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  WHY THEY LOVE IT (dark band)                                       */
/* ------------------------------------------------------------------ */
const LOVE_ITEMS = [
  { icon: Gauge, title: "40% Faster", sub: "Performance", color: "text-[#A78BFA]" },
  { icon: Bot, title: "AI-Powered", sub: "Legal Assistant", color: "text-[#34D399]" },
  { icon: BarChart3, title: "Advanced", sub: "Analytics", color: "text-[#60A5FA]" },
  { icon: Workflow, title: "Smart", sub: "Automations", color: "text-[#F2B22E]" },
  { icon: Users, title: "Better Team", sub: "Collaboration", color: "text-[#F471B5]" },
  { icon: ShieldCheck, title: "Enterprise Grade", sub: "Security", color: "text-[#22D3EE]" },
];

function LoveSection() {
  return (
    <section className="bg-white pb-16">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <div
          className="relative overflow-hidden rounded-2xl px-6 sm:px-10 py-10"
          style={{
            background:
              "radial-gradient(700px 300px at 20% 0%, rgba(94,50,180,0.5) 0%, rgba(94,50,180,0) 60%), radial-gradient(700px 320px at 85% 110%, rgba(70,42,160,0.55) 0%, rgba(70,42,160,0) 62%), linear-gradient(180deg, #150B33 0%, #0C0524 100%)",
          }}
        >
          <h2 className="text-center text-white text-[22px] sm:text-[24px] font-bold">
            Why Legal Professionals Love Jurident 2.0
          </h2>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {LOVE_ITEMS.map(({ icon: Icon, title, sub, color }) => (
              <div key={title} className="flex items-center gap-3">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-white/[0.07] border border-white/10 flex items-center justify-center">
                  <Icon className={`w-[18px] h-[18px] ${color}`} />
                </div>
                <div className="leading-tight">
                  <p className="text-white text-[12.5px] font-semibold">{title}</p>
                  <p className="text-[#A99CC7] text-[12.5px]">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FEATURES GRID                                                      */
/* ------------------------------------------------------------------ */
const FEATURES = [
  {
    icon: Bot,
    tint: "bg-[#EEF0FF] text-[#5B4BE7]",
    title: "AI Legal Assistant",
    desc: "Draft documents, research laws, summarize judgments and find precedents instantly.",
    badge: "New",
  },
  {
    icon: LayoutDashboard,
    tint: "bg-[#E8F1FF] text-[#3B82F6]",
    title: "Smart Dashboard",
    desc: "Real-time overview of cases, hearings, tasks, revenue and team activities.",
  },
  {
    icon: ClipboardList,
    tint: "bg-[#FFEBF0] text-[#EC4899]",
    title: "Task Management",
    desc: "Kanban boards, calendar view, timelines and automated reminders.",
  },
  {
    icon: Users,
    tint: "bg-[#E7F8F0] text-[#10B981]",
    title: "Team Workspace",
    desc: "Departments, roles, permissions, shared cases and activity feed.",
  },
  {
    icon: Monitor,
    tint: "bg-[#E8F1FF] text-[#3B82F6]",
    title: "Client Portal",
    desc: "Keep clients updated with real-time case status, documents and invoices.",
  },
  {
    icon: FileSearch,
    tint: "bg-[#EEF0FF] text-[#5B4BE7]",
    title: "Document Manager",
    desc: "AI search, OCR, version history, secure cloud storage and tagging.",
  },
  {
    icon: Receipt,
    tint: "bg-[#FFF1E6] text-[#F97316]",
    title: "Billing & Invoicing",
    desc: "Create invoices, track payments, generate reports and more.",
  },
  {
    icon: Wallet,
    tint: "bg-[#FEF6E0] text-[#D9A21B]",
    title: "Expense Tracking",
    desc: "Track expenses, categorize and generate expense reports easily.",
  },
  {
    icon: CalendarClock,
    tint: "bg-[#E8F1FF] text-[#3B82F6]",
    title: "Court Calendar Sync",
    desc: "Sync court dates, hearings and deadlines automatically with alerts.",
  },
  {
    icon: Lock,
    tint: "bg-[#E7F8F0] text-[#10B981]",
    title: "Secure & Reliable",
    desc: "Enterprise grade security, daily backups and 99.9% uptime assurance.",
  },
];

function FeaturesSection() {
  return (
    <section className="bg-white pb-16">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <h2 className="text-center text-[26px] sm:text-[28px] font-bold text-[#161231]">
          Powerful Features, Built for Law Practice
        </h2>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {FEATURES.map(({ icon: Icon, tint, title, desc, badge }) => (
            <div
              key={title}
              className="relative rounded-xl border border-[#EBECF2] bg-white p-5 hover:shadow-[0_12px_30px_rgba(17,17,45,0.08)] hover:-translate-y-0.5 transition-all"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}>
                <Icon className="w-[18px] h-[18px]" />
              </div>
              <h4 className="mt-3.5 text-[13.5px] font-semibold text-[#161231]">
                {title}
              </h4>
              <p className="mt-2 text-[11.5px] leading-[1.65] text-[#7B8095]">
                {desc}
              </p>
              {badge && (
                <span className="absolute bottom-4 right-4 flex items-center gap-1 px-2 py-[3px] rounded-full bg-[#FEF6E0] text-[#B8860B] text-[9px] font-semibold">
                  <Sparkles className="w-2.5 h-2.5" /> {badge}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  OLD vs NEW                                                         */
/* ------------------------------------------------------------------ */
const OLD_POINTS = [
  "Basic Dashboard",
  "Simple Case Management",
  "Limited Reports",
  "Traditional Interface",
];

const NEW_POINTS = [
  "Modern Dashboard",
  "AI-Powered Workspace",
  "Smart Automation",
  "Beautiful & Intuitive UI",
  "Advanced Analytics",
  "Mobile Optimized",
];

function DifferenceSection() {
  return (
    <section className="bg-white pb-16">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <h2 className="text-center text-[26px] sm:text-[28px] font-bold text-[#161231]">
          Experience the Difference
        </h2>

        <div className="mt-8 flex flex-col lg:flex-row items-stretch gap-4">
          {/* OLD */}
          <div className="flex-1 rounded-2xl bg-[#F1F2F7] border border-[#E7E8EF] p-6">
            <div className="flex flex-col sm:flex-row gap-5 sm:gap-6">
              <div className="sm:min-w-[190px]">
                <h4 className="text-[15px] font-semibold text-[#3F4354]">
                  Old Jurident Portal
                </h4>
                <ul className="mt-4 space-y-2.5">
                  {OLD_POINTS.map((p) => (
                    <li
                      key={p}
                      className="flex items-center gap-2 text-[12px] text-[#7B8095]"
                    >
                      <Check className="w-3.5 h-3.5 text-[#9CA3AF]" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 rounded-lg overflow-hidden border border-[#E1E2EA] bg-white self-center">
                <img
                  src={SHOTS.oldPortal}
                  alt="Old Jurident portal dashboard"
                  loading="lazy"
                  className="w-full block opacity-80"
                />
              </div>
            </div>
          </div>

          {/* VS */}
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white border border-[#E7E8EF] shadow-[0_6px_18px_rgba(17,17,45,0.10)] flex items-center justify-center text-[13px] font-bold text-[#6B7280]">
              VS
            </div>
          </div>

          {/* NEW */}
          <div
            className="relative flex-1 rounded-2xl p-6 overflow-hidden"
            style={{
              background:
                "radial-gradient(500px 260px at 15% 0%, rgba(96,52,184,0.45) 0%, rgba(96,52,184,0) 65%), linear-gradient(180deg, #150B33 0%, #0C0524 100%)",
            }}
          >
            <span className="absolute top-4 right-4 px-2.5 py-[3px] rounded-md bg-[#5B4BE7] text-white text-[10px] font-semibold">
              New
            </span>
            <div className="flex flex-col sm:flex-row gap-5 sm:gap-6">
              <div className="sm:min-w-[190px]">
                <h4 className="text-[15px] font-semibold text-white">
                  New Jurident 2.0
                </h4>
                <ul className="mt-4 space-y-2.5">
                  {NEW_POINTS.map((p) => (
                    <li
                      key={p}
                      className="flex items-center gap-2 text-[12px] text-[#D6CFEA]"
                    >
                      <Check className="w-3.5 h-3.5 text-[#F2B22E]" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 rounded-lg overflow-hidden border border-white/10 self-center">
                <img
                  src={SHOTS.dashboard}
                  alt="New Jurident 2.0 dashboard"
                  loading="lazy"
                  className="w-full block"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  TESTIMONIALS                                                       */
/* ------------------------------------------------------------------ */
const TESTIMONIALS = [
  {
    quote:
      "Jurident 2.0 has completely changed how we manage our cases. The AI features save us hours every day.",
    name: "Rohit Malhotra",
    role: "Partner",
    tint: "from-[#5B4BE7] to-[#8B6BF0]",
  },
  {
    quote:
      "The new dashboard and task management system is incredibly intuitive and powerful.",
    name: "Adv. Neha Sharma",
    role: "Senior Advocate",
    tint: "from-[#EC4899] to-[#F472B6]",
  },
  {
    quote:
      "Client portal and billing features are outstanding. Our clients love the real-time updates.",
    name: "Arjun Mehta",
    role: "Managing Partner",
    tint: "from-[#10B981] to-[#34D399]",
  },
  {
    quote:
      "A must-have for every modern law firm. The performance improvement is remarkable.",
    name: "Sai Krishnan",
    role: "Legal Consultant",
    tint: "from-[#F97316] to-[#FBBF24]",
  },
];

function initials(name) {
  return name
    .replace(/^Adv\.\s*/i, "")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
}

function TestimonialsSection() {
  const [start, setStart] = useState(0);

  const shift = (dir) =>
    setStart((i) => (i + dir + TESTIMONIALS.length) % TESTIMONIALS.length);

  const ordered = [...TESTIMONIALS.slice(start), ...TESTIMONIALS.slice(0, start)];

  return (
    <section className="bg-white pb-16">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <h2 className="text-center text-[26px] sm:text-[28px] font-bold text-[#161231]">
          Loved by Legal Professionals
        </h2>

        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={() => shift(-1)}
            aria-label="Previous testimonial"
            className="shrink-0 w-9 h-9 rounded-full border border-[#E6E7EE] bg-white flex items-center justify-center text-[#6B7280] hover:bg-[#F6F7FB] transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {ordered.map((t) => (
              <div
                key={t.name}
                className="rounded-xl border border-[#EBECF2] bg-white p-5 flex flex-col"
              >
                <span className="text-[26px] leading-none text-[#D9DBE6] font-serif">
                  “
                </span>
                <p className="mt-1 text-[12px] leading-[1.7] text-[#5B6072] flex-1">
                  {t.quote}
                </p>
                <div className="mt-5 flex items-center gap-2.5">
                  <div
                    className={`w-8 h-8 rounded-full bg-gradient-to-br ${t.tint} flex items-center justify-center text-white text-[11px] font-semibold`}
                  >
                    {initials(t.name)}
                  </div>
                  <div className="leading-tight">
                    <p className="text-[12px] font-semibold text-[#161231]">
                      {t.name}
                    </p>
                    <p className="text-[10.5px] text-[#8A8FA3]">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => shift(1)}
            aria-label="Next testimonial"
            className="shrink-0 w-9 h-9 rounded-full border border-[#E6E7EE] bg-white flex items-center justify-center text-[#6B7280] hover:bg-[#F6F7FB] transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FINAL CTA                                                          */
/* ------------------------------------------------------------------ */
function CtaSection({ navigate }) {
  return (
    <section className="bg-white pb-16">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
        <div
          className="relative overflow-hidden rounded-2xl px-6 sm:px-10 py-9"
          style={{
            background:
              "radial-gradient(600px 280px at 12% 0%, rgba(96,52,184,0.5) 0%, rgba(96,52,184,0) 62%), radial-gradient(600px 300px at 92% 110%, rgba(120,60,200,0.45) 0%, rgba(120,60,200,0) 60%), linear-gradient(180deg, #150B33 0%, #0B0424 100%)",
          }}
        >
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-7">
            <div>
              <h2 className="text-white text-[24px] sm:text-[27px] font-bold">
                Ready to Upgrade Your Legal Practice?
              </h2>
              <p className="mt-2 text-[13px] text-[#B9AFD3]">
                Join 1200+ legal professionals who are already using Jurident 2.0
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate("/portal")}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#F2B22E] text-[#1A1033] text-[14px] font-semibold hover:bg-[#e0a41f] transition-colors"
              >
                Launch Jurident 2.0 <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/demo")}
                className="px-6 py-3 rounded-lg border border-white/25 bg-white/[0.06] text-white text-[14px] font-medium hover:bg-white/10 transition-colors"
              >
                Schedule a Demo
              </button>
            </div>

            <div className="hidden xl:block shrink-0 pr-2">
              <Rocket className="w-12 h-12 text-[#F2B22E] rotate-[18deg] drop-shadow-[0_0_18px_rgba(242,178,46,0.55)]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
export default function NewUILanding() {
  const navigate = useNavigate();

  return (
    <div style={FONT} className="bg-white min-h-screen">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <Hero navigate={navigate} />
      <TrustSection />
      <ExploreSection />
      <LoveSection />
      <FeaturesSection />
      <DifferenceSection />
      <TestimonialsSection />
      <CtaSection navigate={navigate} />
    </div>
  );
}
